<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Animierter Background -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 dark:from-gray-900 dark:via-blue-950/20 dark:to-gray-800"></div>
      
      <!-- Animierte Gradient Blobs -->
      <div class="absolute top-0 -left-4 w-96 h-96 bg-blue-400/20 dark:bg-blue-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob"></div>
      <div class="absolute top-0 -right-4 w-96 h-96 bg-purple-400/20 dark:bg-purple-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-8 left-20 w-96 h-96 bg-pink-400/20 dark:bg-pink-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <!-- Diagonal Lines Pattern instead of Grid -->
      <div class="absolute inset-0 opacity-40 dark:opacity-20" style="background-image: repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(59, 130, 246, 0.03) 20px, rgba(59, 130, 246, 0.03) 21px);"></div>
      <div class="absolute inset-0 opacity-30 dark:opacity-15" style="background-image: repeating-linear-gradient(-45deg, transparent, transparent 20px, rgba(147, 197, 253, 0.03) 20px, rgba(147, 197, 253, 0.03) 21px);"></div>
      
      <!-- Radial Lines Pattern -->
      <div class="absolute inset-0 opacity-20 dark:opacity-10" style="background: radial-gradient(circle at 30% 30%, rgba(59, 130, 246, 0.05) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(147, 197, 253, 0.05) 0%, transparent 50%);"></div>
    </div>
    
    <Navigation />
    <!-- Hero Section -->
    <section class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32 md:pt-40 pb-16 md:pb-24">
      <div class="relative">
        <!-- Desktop Layout: 2 Spalten (wie auf Startseite) -->
        <div class="hidden lg:grid lg:grid-cols-12 gap-16 items-center mb-24">
          <!-- Linke Spalte: Content (Text & Info) -->
          <div class="col-span-7 flex flex-col justify-center order-1">
            <!-- Header -->
            <div 
              class="mb-10 transition-all duration-600 ease-out"
              :class="headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
            >
              <h1 class="text-4xl xl:text-5xl font-extrabold text-gray-900 dark:text-gray-100 mb-4 tracking-tight">
                Über mich
              </h1>
              <div class="h-0.5 w-16 bg-blue-600 dark:bg-blue-400"></div>
            </div>

            <!-- Intro Text -->
            <div 
              class="mb-10 transition-all duration-600 ease-out"
              :class="introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
            >
              <p class="text-xl xl:text-2xl text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Mein Name ist <span class="font-bold text-gray-900 dark:text-gray-100">Maximilian Reitsberger</span>. Als gelernter
                Fachinformatiker, Senior QA Specialist und Webentwickler aus München verbinde ich
                <span class="font-semibold text-blue-600 dark:text-blue-400">moderne Gestaltung mit sauberer technischer Umsetzung</span>.
              </p>
              <p class="text-lg xl:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                Ich entwickle Websites und individuelle Weblösungen für Unternehmen, Vereine und Selbstständige - mit Fokus auf klare Struktur,
                einfache Bedienbarkeit und zuverlässige Funktion im Alltag.
              </p>
            </div>

            <!-- Expertise -->
            <div 
              class="mb-12 transition-all duration-600 ease-out"
              :class="expertiseVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
            >
              <h3 class="text-2xl xl:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Meine Expertise</h3>
              
              <div class="space-y-6">
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0 w-3 h-3 rounded-full bg-blue-600 dark:bg-blue-400 mt-2" aria-hidden="true"></div>
                  <div>
                    <p class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Qualitätssicherung</p>
                    <p class="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                      Ich achte darauf, dass Websites nicht nur gut aussehen, sondern auch stabil laufen, verständlich aufgebaut sind und auf verschiedenen Geräten sauber funktionieren.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0 w-3 h-3 rounded-full bg-blue-600 dark:bg-blue-400 mt-2" aria-hidden="true"></div>
                  <div>
                    <p class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Webentwicklung</p>
                    <p class="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                      Ich erstelle moderne Websites, WordPress-Lösungen und individuelle Tools, die genau zu den Anforderungen des jeweiligen Projekts passen.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Info Badges -->
            <div 
              class="flex flex-wrap gap-3 mb-8 transition-all duration-600 ease-out"
              :class="badgesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
            >
              <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-sm font-bold bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700/50 shadow-sm transition-transform hover:scale-105">
                <AcademicCapIcon class="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                Fachinformatiker
              </span>
              <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-sm font-bold bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700/50 shadow-sm transition-transform hover:scale-105">
                <BriefcaseIcon class="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                Senior QA Specialist
              </span>
              <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-sm font-bold bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700/50 shadow-sm transition-transform hover:scale-105">
                <StarIcon class="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                ISTQB Zertifiziert
              </span>
              <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-sm font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50 shadow-sm transition-transform hover:scale-105">
                <MapPinIcon class="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                München, Deutschland
              </span>
            </div>
          </div>

          <!-- Rechte Spalte: Profilbild (wie auf Startseite) -->
          <div class="col-span-5 flex flex-col items-center order-2">
            <!-- Profilbild mit modernem Effekt -->
            <div 
              v-if="profileImageUrl && !profileImageError"
              class="relative mb-12 group cursor-pointer"
            >
              <!-- Mehrschichtiger Hintergrund-Glow -->
              <div 
                class="absolute inset-0 bg-blue-500/20 dark:bg-blue-400/10 blur-3xl rounded-full transition-all duration-500 ease-out group-hover:scale-150"
                :class="imageGlowVisible ? 'opacity-100 scale-125' : 'opacity-0 scale-105'"
              ></div>
              <div class="absolute -inset-4 bg-gradient-to-tr from-blue-500/10 to-purple-500/10 blur-2xl rounded-[3rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              <!-- Bild Container mit Glassmorphism Rahmen -->
              <div 
                class="relative w-72 h-96 xl:w-84 xl:h-[30rem] overflow-hidden bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-[12px] border-white/80 dark:border-gray-800/80 rounded-[3rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] shadow-blue-500/20 dark:shadow-[0_20px_50px_rgba(0,0,0,0.3)] -rotate-2 transition-all duration-500 ease-out group-hover:-rotate-3 group-hover:scale-[1.02] group-hover:shadow-blue-500/30"
                :class="imageContainerVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-[0.98] translate-y-3'"
              >
                <img 
                  :src="profileImageUrl" 
                  alt="Maximilian Reitsberger"
                  class="w-full h-full object-cover object-[center_15%] scale-105 transition-all duration-500 ease-out group-hover:scale-110"
                  :class="imageContainerVisible ? 'blur-0' : 'blur-[1px]'"
                  loading="eager"
                  fetchpriority="high"
                  @error="handleProfileImageError"
                  @load="handleProfileImageLoad"
                />
                <!-- Subtiles Overlay für Tiefe -->
                <div class="absolute inset-0 bg-gradient-to-t from-blue-900/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>

              <!-- Deko-Element: Floating Dots -->
              <div class="absolute -bottom-6 -left-6 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10 bg-[radial-gradient(#3b82f6_2px,transparent_2px)] [background-size:10px_10px]"></div>
            </div>
          </div>
        </div>
        
        <!-- Mobile Layout: Modernisiert & Zentriert -->
        <div class="lg:hidden text-center flex flex-col items-center">
          <!-- Header -->
          <div 
            class="mb-8 transition-all duration-600 ease-out"
            :class="headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
          >
            <h1 class="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-gray-100 mb-4 tracking-tight">
              Über mich
            </h1>
            <div class="h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"></div>
          </div>

          <!-- Profilbild - Modernisiert -->
          <div 
            v-if="profileImageUrl && !profileImageError"
            class="relative mb-10 group"
          >
            <div 
              class="relative w-64 h-72 rounded-[2.5rem] overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl -rotate-2 transition-all duration-500 ease-out active:scale-95"
              :class="imageContainerVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-[0.98] translate-y-3'"
            >
              <img 
                :src="profileImageUrl" 
                alt="Maximilian Reitsberger" 
                class="w-full h-full object-cover object-[center_15%] scale-105 transition-all duration-500 ease-out"
                :class="imageContainerVisible ? 'blur-0' : 'blur-[1px]'"
                loading="eager"
                fetchpriority="high"
                @error="handleProfileImageError"
                @load="handleProfileImageLoad"
              />
            </div>
            <!-- Hintergrund Glow (Mobile dezenter) -->
            <div 
              class="absolute inset-0 -z-10 bg-blue-400/10 blur-xl rounded-full transition-all duration-500 ease-out"
              :class="imageGlowVisible ? 'opacity-100 scale-110' : 'opacity-0 scale-100'"
            ></div>
          </div>

          <!-- Intro Text Mobile -->
          <div 
            class="mb-8 transition-all duration-600 ease-out max-w-lg"
            :class="introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
          >
            <p class="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 px-4">
              Mein Name ist <span class="font-bold text-gray-900 dark:text-gray-100">Maximilian Reitsberger</span>. Als gelernter
              Fachinformatiker, Senior QA Specialist und Webentwickler aus München verbinde ich
              <span class="font-semibold text-blue-600 dark:text-blue-400">moderne Gestaltung mit sauberer technischer Umsetzung</span>.
            </p>
            <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed px-4">
              Ich entwickle Websites und individuelle Weblösungen für Unternehmen, Vereine und Selbstständige - mit Fokus auf klare Struktur,
              einfache Bedienbarkeit und zuverlässige Funktion im Alltag.
            </p>
          </div>

          <!-- Badges Mobile -->
          <div 
            class="flex flex-wrap justify-center gap-2 mb-8 transition-all duration-600 ease-out"
            :class="badgesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
          >
            <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-xs font-bold bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700/50">
              <AcademicCapIcon class="w-3 h-3 mr-1.5 text-blue-600 dark:text-blue-400" />
              Fachinformatiker
            </span>
            <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-xs font-bold bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700/50">
              <BriefcaseIcon class="w-3 h-3 mr-1.5 text-blue-600 dark:text-blue-400" />
              Senior QA
            </span>
            <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-xs font-bold bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700/50">
              <StarIcon class="w-3 h-3 mr-1.5 text-blue-600 dark:text-blue-400" />
              ISTQB
            </span>
            <span class="inline-flex items-center px-4 py-1.5 rounded-xl text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50">
              <MapPinIcon class="w-3 h-3 mr-1.5 text-blue-600 dark:text-blue-400" />
              München
            </span>
          </div>
          
          <!-- Expertise Mobile -->
          <div 
            class="mb-8 transition-all duration-600 ease-out max-w-lg px-4"
            :class="expertiseVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
          >
            <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Meine Expertise</h3>
            
            <div class="space-y-4 text-left">
              <div class="flex items-start space-x-3">
                <div class="flex-shrink-0 w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400 mt-2" aria-hidden="true"></div>
                <div>
                  <p class="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Qualitätssicherung</p>
                  <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                    Ich achte darauf, dass Websites nicht nur gut aussehen, sondern auch stabil laufen, verständlich aufgebaut sind und auf verschiedenen Geräten sauber funktionieren.
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="flex-shrink-0 w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400 mt-2" aria-hidden="true"></div>
                <div>
                  <p class="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Webentwicklung</p>
                  <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                    Ich erstelle moderne Websites, WordPress-Lösungen und individuelle Tools, die genau zu den Anforderungen des jeweiligen Projekts passen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Timeline Section -->
    <section 
      id="timeline-section" 
      ref="timelineSection" 
      class="py-12 relative overflow-hidden"
      aria-label="Beruflicher Werdegang"
    >
      <!-- Elegant Top Divider -->
      <div class="absolute top-0 left-0 w-full z-20">
        <!-- Subtle curved line -->
        <svg 
          class="relative block w-full h-12" 
          viewBox="0 0 1200 48" 
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="dividerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style="stop-color:rgb(59, 130, 246);stop-opacity:0" />
              <stop offset="45%" style="stop-color:rgb(59, 130, 246);stop-opacity:0.3" />
              <stop offset="50%" style="stop-color:rgb(59, 130, 246);stop-opacity:0.5" />
              <stop offset="55%" style="stop-color:rgb(59, 130, 246);stop-opacity:0.3" />
              <stop offset="100%" style="stop-color:rgb(59, 130, 246);stop-opacity:0" />
            </linearGradient>
          </defs>
          <!-- Subtle curve -->
          <path 
            d="M0,24 Q600,12 1200,24" 
            stroke="url(#dividerGradient)" 
            stroke-width="1.5" 
            fill="none"
            class="dark:opacity-60"
          />
        </svg>
        
        <!-- Center accent dot -->
        <div class="absolute top-6 left-1/2 -translate-x-1/2 z-30">
          <div class="w-2.5 h-2.5 rounded-full bg-blue-500 dark:bg-blue-400 shadow-lg shadow-blue-500/50 dark:shadow-blue-400/50 border-2 border-white dark:border-gray-800"></div>
          <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-blue-500/20 dark:bg-blue-400/20 animate-pulse"></div>
        </div>
      </div>
      
      <!-- Background Pattern - Different from Hero Section -->
      <div class="absolute inset-0 bg-gradient-to-b from-white via-gray-50/50 to-white dark:from-gray-800 dark:via-gray-800/50 dark:to-gray-800"></div>
      
      <!-- Animated Background Elements - Different Style -->
      <div class="absolute inset-0 overflow-hidden">
        <!-- Dot Pattern instead of Grid -->
        <div class="absolute inset-0 opacity-30 dark:opacity-20" style="background-image: radial-gradient(circle, rgba(59, 130, 246, 0.15) 1px, transparent 1px); background-size: 32px 32px; background-position: 0 0, 16px 16px;"></div>
        
        <!-- Different Blob Colors and Positions -->
        <div class="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-indigo-400/4 dark:bg-indigo-500/3 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-50 animate-blob"></div>
        <div class="absolute bottom-1/4 left-1/3 w-[450px] h-[450px] bg-cyan-400/4 dark:bg-cyan-500/3 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
        
        <!-- Vertical Gradient Accent -->
        <div class="absolute left-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-blue-200/20 to-transparent dark:via-blue-800/10"></div>
        <div class="absolute right-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-purple-200/20 to-transparent dark:via-purple-800/10"></div>
      </div>
      
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-8">
        <div class="text-center mb-12">
          <h3 class="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">Mein Werdegang</h3>
          <div class="mt-4 h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"></div>
          <p class="mt-6 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Von technischer Basis über Qualitätsdenken bis zu Webentwicklung mit Anspruch
          </p>
        </div>

        <!-- Timeline -->
        <div class="relative">
          <!-- Vertical Line Background -->
          <div class="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-gray-200 to-gray-200 dark:via-gray-700 dark:to-gray-700 transform md:-translate-x-1/2"></div>
          
          <!-- Animated Progress Line -->
          <div 
            class="absolute left-8 md:left-1/2 top-0 w-0.5 bg-gradient-to-b from-blue-600 via-blue-400 to-blue-600 transform md:-translate-x-1/2 transition-all duration-700 ease-out origin-top shadow-sm"
            :style="{ height: `${timelineProgress}%` }"
          ></div>
          
          <!-- Top Connection Point -->
          <div class="absolute left-8 md:left-1/2 top-0 transform md:-translate-x-1/2 -translate-y-1/2 z-30">
            <div class="w-3 h-3 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg border-2 border-white dark:border-gray-800"></div>
            <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-blue-500/30 animate-pulse"></div>
          </div>

          <!-- Timeline Items -->
          <div>
            <div 
              v-for="(item, index) in timelineItems" 
              :key="index"
              :ref="el => { if (el) timelineItemRefs[index] = el as HTMLElement }"
              class="relative flex items-start md:items-center mb-10 last:mb-0 transition-all duration-700"
              :class="[
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse',
                visibleItems[index] 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              ]"
              :style="{ zIndex: timelineItems.length - index }"
            >
              <!-- Timeline Dot -->
              <div class="absolute left-8 md:left-1/2 transform -translate-x-1/2 z-20">
                <div 
                  class="relative rounded-full border-4 border-white dark:border-gray-800 shadow-lg transition-all duration-500"
                  :class="[
                    item.isCTA ? 'w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600' : 'w-5 h-5',
                    visibleItems[index] ? 'scale-100' : 'scale-0',
                    item.isCTA ? '' : (visibleItems[index] ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'),
                    activeItemIndex === index && !item.isCTA ? 'scale-125 ring-4 ring-blue-200 dark:ring-blue-800 bg-blue-500' : '',
                    hoveredIndex === index && !item.isCTA ? 'scale-150 bg-blue-500 ring-4 ring-blue-200 dark:ring-blue-800' : '',
                    item.isCTA ? 'ring-4 ring-blue-300/50 dark:ring-blue-700/50 animate-pulse' : ''
                  ]"
                >
                  <div 
                    v-if="item.isCTA"
                    class="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75"
                  ></div>
                  <div 
                    v-else-if="activeItemIndex === index || hoveredIndex === index"
                    class="absolute inset-0 rounded-full bg-blue-400 animate-ping"
                  ></div>
                </div>
                <!-- Year Badge on Timeline -->
                <div 
                  v-if="activeItemIndex === index"
                  class="absolute left-1/2 -translate-x-1/2 -top-12 whitespace-nowrap transition-all duration-300"
                >
                  <div class="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    {{ item.year }}
                  </div>
                  <div class="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-600"></div>
                </div>
              </div>

              <!-- Content Card -->
              <div 
                class="ml-20 md:ml-0 md:w-5/12 relative"
                :class="[
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                ]"
              >
                <a 
                  v-if="item.isCTA"
                  href="/contact"
                  class="block w-full"
                >
                  <div 
                    class="w-full bg-gradient-to-br from-blue-600 to-blue-500 border-2 border-blue-400 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 text-white cursor-pointer"
                    @mouseenter="hoveredIndex = index"
                    @mouseleave="hoveredIndex = null"
                  >
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-sm font-semibold text-blue-100 bg-white/20 px-3 py-1 rounded-full">
                        {{ item.year }}
                      </span>
                      <component 
                        :is="item.icon" 
                        class="h-7 w-7 text-white flex-shrink-0"
                      />
                    </div>
                    <h4 class="text-2xl font-bold text-white mb-2">
                      {{ item.title }}
                    </h4>
                    <p class="text-blue-50 leading-relaxed">{{ item.description }}</p>
                    <div class="mt-4 pt-4 border-t border-white/20">
                      <span class="text-sm font-medium text-white inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                        Kostenloses Erstgespräch anfragen
                        <svg class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </a>
                <div 
                  v-else
                  class="w-full bg-white dark:bg-gray-800 border-2 rounded-lg p-6 shadow-sm dark:shadow-gray-900/50 hover:shadow-md dark:hover:shadow-gray-900/70 transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-600 dark:focus-within:ring-blue-400 focus-within:ring-offset-2"
                  :class="[
                    activeItemIndex === index 
                      ? 'border-blue-400 dark:border-blue-500 shadow-lg dark:shadow-gray-900/70 z-30' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 z-10'
                  ]"
                  @mouseenter="hoveredIndex = index"
                  @mouseleave="hoveredIndex = null"
                  tabindex="0"
                  role="article"
                  :aria-label="`${item.year}: ${item.title}`"
                  @keydown.enter="hoveredIndex = index"
                  @keydown.escape="hoveredIndex = null"
                >
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full transition-all duration-300"
                          :class="activeItemIndex === index ? 'bg-blue-100 dark:bg-blue-900/30 scale-110' : ''">
                      {{ item.year }}
                    </span>
                    <component 
                      :is="item.icon" 
                      class="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 transition-transform duration-300"
                      :class="activeItemIndex === index ? 'scale-110 rotate-3' : ''"
                    />
                  </div>
                  <h4 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-300"
                      :class="activeItemIndex === index ? 'text-blue-600 dark:text-blue-400' : ''">
                    {{ item.title }}
                  </h4>
                  <p class="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">{{ item.description }}</p>
                  <div v-if="item.details" class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <p class="text-sm text-gray-600 dark:text-gray-400 italic">{{ item.details }}</p>
                  </div>
                  <div v-if="item.isPortfolio" class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <router-link 
                      to="/portfolio"
                      class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 focus:ring-offset-2"
                      aria-label="Projekte ansehen"
                    >
                      Projekte ansehen
                      <svg class="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <StandardFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { 
  MapPinIcon, 
  BriefcaseIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CodeBracketIcon,
  StarIcon,
  RocketLaunchIcon
} from '@heroicons/vue/24/outline'
import Navigation from '../components/Navigation.vue'
import StandardFooter from '../components/StandardFooter.vue'
import { logger } from '../utils/logger'

// About-Seite Bilder
const fallbackProfileImageUrl = '/images/profile-hero.webp'
const aboutImages = ref<Array<{ url: string; name: string }>>([])
const profileImageUrl = ref<string | null>(fallbackProfileImageUrl)
const profileImageError = ref(false)

// Animation States
const headerVisible = ref(false)
const imageContainerVisible = ref(false)
const imageGlowVisible = ref(false)
const introVisible = ref(false)
const expertiseVisible = ref(false)
const badgesVisible = ref(false)

const handleProfileImageError = () => {
  if (profileImageUrl.value !== fallbackProfileImageUrl) {
    profileImageUrl.value = fallbackProfileImageUrl
    profileImageError.value = false
    return
  }

  profileImageError.value = true
}

const handleProfileImageLoad = () => {
  profileImageError.value = false
}

// Lade Bilder aus Firebase
import { getPublicImageByName, getPublicImagesByCategory } from '../firebase/images'

// Fügt einen Preload-Link für das Profilbild hinzu (optimiert Ladezeit) mit automatischer Bereinigung
const addImagePreload = (url: string) => {
  if (typeof document === 'undefined') return
  
  // Prüfe, ob bereits ein Preload-Link existiert
  const existingPreload = document.querySelector(`link[rel="preload"][as="image"][href="${url}"]`)
  if (existingPreload) return
  
  // Erstelle Preload-Link
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = url
  link.setAttribute('fetchpriority', 'high')
  document.head.appendChild(link)
  
  // Entferne Preload-Link, sobald das Bild geladen wurde oder nach 5 Sekunden
  const img = new Image()
  const timeout = setTimeout(() => {
    link.remove()
  }, 5000) // Entferne nach 5 Sekunden, falls nicht verwendet
  
  img.onload = () => {
    clearTimeout(timeout)
    // Entferne Preload-Link, sobald Bild geladen wurde
    link.remove()
  }
  img.onerror = () => {
    clearTimeout(timeout)
    link.remove()
  }
  img.src = url
}

const preloadImage = (url: string): Promise<void> => {
  if (typeof Image === 'undefined') return Promise.resolve()

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = reject
    img.src = url
  })
}

const loadAboutImages = async () => {
  try {
    // Versuche zuerst ein Bild mit dem Namen "profile" zu finden
    const profileImage = await getPublicImageByName('about', 'profile')
    if (profileImage) {
      // Preload-Link für das Bild hinzufügen (optimiert Ladezeit)
      addImagePreload(profileImage.url)
      await preloadImage(profileImage.url)
      profileImageUrl.value = profileImage.url
    } else {
      // Fallback: Nimm das erste verfügbare Bild
      const images = await getPublicImagesByCategory('about')
      if (images.length > 0) {
        // Preload-Link für das Bild hinzufügen
        addImagePreload(images[0].url)
        await preloadImage(images[0].url)
        profileImageUrl.value = images[0].url
      }
    }
    
    // Lade alle About-Bilder für weitere Verwendung
    const images = await getPublicImagesByCategory('about')
    aboutImages.value = images.map(img => ({ url: img.url, name: img.name }))
  } catch (error) {
    logger.error('Fehler beim Laden der About-Bilder', error)
  }
}

const hoveredIndex = ref<number | null>(null)
const activeItemIndex = ref<number | null>(null)
const activeYear = ref<string | null>(null)
const timelineProgress = ref<number>(0)
const isTimelineVisible = ref<boolean>(false)
const visibleItems = ref<boolean[]>([])
const timelineSection = ref<HTMLElement | null>(null)
const timelineItemRefs = ref<(HTMLElement | null)[]>([])

// Debounce-Funktion für Scroll-Handler
let scrollTimeout: ReturnType<typeof setTimeout> | null = null
const debounceDelay = 10

interface TimelineItem {
  year: string
  title: string
  description: string
  details?: string
  icon: InstanceType<any>
  isCTA?: boolean
  isPortfolio?: boolean
}

const timelineItems: TimelineItem[] = [
  {
    year: '2019',
    title: 'Fachinformatiker Ausbildung abgeschlossen',
    description: 'Abschluss der Ausbildung zum Fachinformatiker mit Fokus auf Systemintegration.',
    icon: AcademicCapIcon
  },
  {
    year: '2019',
    title: 'Technisches Verständnis vertieft',
    description: 'Einstieg in den IT-Support mit engem Austausch zu Entwicklung, Systemen und Anwendern.',
    icon: BuildingOfficeIcon
  },
  {
    year: '2020',
    title: 'Einstieg Qualitätssicherung',
    description: 'Mithilfe beim Aufbau eines QA-Teams und Etablierung systematischer Software-Tests.',
    icon: ChartBarIcon
  },
  {
    year: '2020 - heute',
    title: 'Erste Webprojekte',
    description: 'Gründung des Kleingewerbes und Entwicklung individueller Websites und Weblösungen.',
    icon: CodeBracketIcon,
    isPortfolio: true
  },
  {
    year: '2021',
    title: 'ISTQB-Zertifizierung',
    description: 'Erwerb der ISTQB-Zertifizierung zur Vertiefung des Fachwissens im Bereich Quality Assurance.',
    icon: StarIcon
  },
  {
    year: '2023-heute',
    title: 'Senior QA Specialist',
    description: 'Übernahme zusätzlicher Verantwortung für Qualitätsstandards, Testprozesse und zuverlässige Software.',
    icon: ChartBarIcon
  },
  {
    year: new Date().getFullYear().toString(),
    title: 'Ihr Projekt?',
    description: 'Haben Sie eine Idee für eine Website oder ein Webprojekt? Ich unterstütze Sie gerne bei der Umsetzung Ihrer Vision.',
    icon: RocketLaunchIcon,
    isCTA: true
  }
]

// Initialisiere visibleItems Array
visibleItems.value = new Array(timelineItems.length).fill(false)

// Extrahiert nur die Jahreszahl aus Strings wie "2023-heute" oder "2020-heute"
const extractYear = (yearString: string): string => {
  const match = yearString.match(/^\d{4}/)
  return match ? match[0] : yearString
}

// Intersection Observer für Timeline-Items
let itemObservers: IntersectionObserver[] = []

const setupItemObservers = () => {
  itemObservers = timelineItemRefs.value
    .filter((ref): ref is HTMLElement => ref !== null)
    .map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleItems.value[index] = true
            }
          })
        },
        {
          threshold: 0.2,
          rootMargin: '-50px 0px -50px 0px'
        }
      )
      observer.observe(ref)
      return observer
    })
}

// Scroll-Handler für aktives Item und Progress (mit Debouncing)
const handleScroll = () => {
  if (scrollTimeout) {
    clearTimeout(scrollTimeout)
  }
  
  scrollTimeout = setTimeout(() => {
    if (!timelineSection.value) return

    const section = timelineSection.value
    const sectionTop = section.offsetTop
    const sectionHeight = section.offsetHeight
    const windowHeight = window.innerHeight
    const scrollY = window.scrollY

    // Prüfe ob Timeline-Section sichtbar ist
    const sectionBottom = sectionTop + sectionHeight
    const isVisible = scrollY + windowHeight > sectionTop && scrollY < sectionBottom
    isTimelineVisible.value = isVisible

    if (!isVisible) {
      activeItemIndex.value = null
      activeYear.value = null
      timelineProgress.value = 0
      return
    }

    // Berechne Progress basierend auf Scroll-Position
    const scrollProgress = Math.max(0, Math.min(1, (scrollY + windowHeight - sectionTop) / sectionHeight))
    timelineProgress.value = scrollProgress * 100

    // Finde aktives Item basierend auf Scroll-Position
    let activeIndex: number | null = null
    const itemPositions = timelineItemRefs.value
      .filter((ref): ref is HTMLElement => ref !== null)
      .map((ref, index) => {
        const rect = ref.getBoundingClientRect()
        const itemCenter = rect.top + rect.height / 2
        const viewportCenter = windowHeight / 2
        return {
          index,
          distance: Math.abs(itemCenter - viewportCenter),
          top: rect.top
        }
      })

    // Finde das Item, das am nächsten zum Viewport-Zentrum ist
    if (itemPositions.length > 0) {
      const sortedByDistance = [...itemPositions].sort((a, b) => a.distance - b.distance)
      const closestItem = sortedByDistance[0]
      
      // Nur als aktiv markieren, wenn es im Viewport ist
      if (closestItem.top < windowHeight && closestItem.top > -200) {
        activeIndex = closestItem.index
      }
    }

    activeItemIndex.value = activeIndex
    if (activeIndex !== null) {
      activeYear.value = extractYear(timelineItems[activeIndex].year)
    } else {
      activeYear.value = null
    }
  }, debounceDelay)
}

onMounted(async () => {
  // Starte Bildladen sofort - parallel zu Text-Animationen (optimiert Ladezeit)
  loadAboutImages().catch((error) => {
    logger.error('Fehler beim Laden der Bilder', error)
  })
  
  // Starte Text-Animationen sofort - nicht auf Bilder warten
  // Optimierte Delays für flüssigere Animation
  setTimeout(() => {
    headerVisible.value = true
    imageContainerVisible.value = true
    imageGlowVisible.value = true
  }, 100)
  
  setTimeout(() => {
    introVisible.value = true
  }, 200)
  
  setTimeout(() => {
    expertiseVisible.value = true
  }, 300)
  
  setTimeout(() => {
    badgesVisible.value = true
  }, 400)

  // Warte kurz, damit DOM gerendert ist
  setTimeout(() => {
    setupItemObservers()
    handleScroll()
  }, 100)

  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('resize', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', handleScroll)
  itemObservers.forEach(observer => observer.disconnect())
  if (scrollTimeout) {
    clearTimeout(scrollTimeout)
  }
})
</script>
